import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaCheckCircle,
  FaCopy,
  FaEdit,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaKey,
  FaLock,
  FaLockOpen,
  FaPlug,
  FaPlus,
  FaStar,
  FaTimes,
  FaTimesCircle,
  FaTrash,
  FaUnlockAlt,
  FaUserShield,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import PasswordInput from "../PasswordInput";
import {
  ActivityButton,
  AuditTable,
  InfoTip,
  apiError,
  RowAction,
  fmtWhen,
  useCompactView,
  useVaultAudit,
} from "./VaultUi";
import "./VaultPanel.css";

const SECRET_HINTS = ["password", "secret", "token", "api key", "apikey", "private key"];
const isSecretLabel = (label) => SECRET_HINTS.some((h) => String(label || "").toLowerCase().includes(h));
const CATEGORY_ICONS = { Email: FaEnvelope, API: FaPlug, Login: FaUserShield, Other: FaKey };
const DEFAULT_CATEGORIES = ["Email", "API", "Login", "Other"];

function presetEntries(meta, category) {
  return (meta?.presets?.[category] || []).map((p) => ({ label: p.label, value: p.value || "" }));
}

/** Staff only: give the client owner a temporary password (on the client's request). */
function OwnerPasswordReset({ basePath, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const reset = async (e) => {
    e.preventDefault();
    if (!window.confirm("Reset the client owner's login password to a temporary one? Their current password stops working.")) return;
    setBusy(true);
    setError("");
    try {
      const res = await axiosInstance.post(`${basePath}/owner-password-reset`, { reason: reason.trim() });
      setResult(res.data?.data);
      setReason("");
      onDone?.();
    } catch (err) {
      setError(apiError(err, "Could not reset the password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vault-reset">
      <strong>Client locked out?</strong>
      <p className="vault-hint">
        Only when the client asks: reset the owner&apos;s login password to a temporary one. It lets them sign in but
        cannot open the vault; they must change it to their own password first. The reset and your reason are recorded
        in the activity log.
      </p>
      {result ? (
        <div className="vault-alert vault-alert--ok">
          Temporary password for <strong>{result.login_email}</strong> (shown once, share it only with the client):{" "}
          <SecretValue value={result.temporary_password} secret />
        </div>
      ) : null}
      {error ? <div className="vault-alert vault-alert--error">{error}</div> : null}
      <form className="vault-reset__form" onSubmit={reset}>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason / client request reference (required)"
          required
          minLength={5}
        />
        <button type="submit" className="vault-btn vault-btn--danger" disabled={busy || reason.trim().length < 5}>
          <FaUnlockAlt /> {busy ? "Resetting…" : "Reset owner password"}
        </button>
      </form>
    </div>
  );
}

function PasswordPrompt({ busy, error, onSubmit, onCancel, text }) {
  const [password, setPassword] = useState("");
  return (
    <form
      className="vault-prompt"
      onSubmit={(e) => {
        e.preventDefault();
        if (password) onSubmit(password);
      }}
    >
      <FaLock aria-hidden="true" />
      <span className="vault-prompt__text">{text}</span>
      <PasswordInput
        wrapperClassName="vault-prompt__password"
        autoFocus
        autoComplete="current-password"
        placeholder="Your login password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />
      <button type="submit" className="vault-btn vault-btn--primary" disabled={busy || !password}>
        {busy ? "Checking…" : "Unlock"}
      </button>
      <button type="button" className="vault-btn" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      {error ? <span className="vault-prompt__error">{error}</span> : null}
    </form>
  );
}

function SecretValue({ value, secret }) {
  const [shown, setShown] = useState(!secret);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (_) {
      /* clipboard blocked */
    }
  };
  return (
    <span className="vault-value">
      <code>{shown ? value || "—" : "••••••••"}</code>
      {secret ? (
        <button type="button" className="vault-icon" title={shown ? "Hide" : "Show"} onClick={() => setShown((v) => !v)}>
          {shown ? <FaEyeSlash /> : <FaEye />}
        </button>
      ) : null}
      {value ? (
        <button type="button" className="vault-icon" title={copied ? "Copied" : "Copy"} onClick={copy}>
          {copied ? <FaCheckCircle /> : <FaCopy />}
        </button>
      ) : null}
    </span>
  );
}

function VaultForm({ meta, item, busy, onSave, onCancel }) {
  const editing = !!item?.Vault_Id;
  const categories = meta?.categories || DEFAULT_CATEGORIES;
  const initialCategory = item?.Category || categories[0];
  const [name, setName] = useState(item?.Name || "");
  const [category, setCategory] = useState(initialCategory);
  const [entries, setEntries] = useState(() =>
    item?.Entries?.length ? item.Entries.map(({ label, value }) => ({ label, value })) : presetEntries(meta, initialCategory)
  );
  const [isDefault, setIsDefault] = useState(!!item?.Is_Default);
  const [reveal, setReveal] = useState({});
  const pristine = !editing && entries.every((e) => !e.value || presetEntries(meta, category).some((p) => p.label === e.label && p.value === e.value));

  const changeCategory = (next) => {
    setCategory(next);
    if (pristine) setEntries(presetEntries(meta, next));
  };
  const setEntry = (i, key, value) => setEntries((list) => list.map((e, idx) => (idx === i ? { ...e, [key]: value } : e)));

  return (
    <form
      className="vault-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({
          Name: name.trim(),
          Category: category,
          Entries: entries.filter((x) => x.label.trim() || x.value),
          ...(category === "Email" ? { Is_Default: isDefault } : {}),
        });
      }}
    >
      <div className="vault-form__grid">
        <label>
          Name *
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales Gmail, Razorpay" />
        </label>
        {categories.length > 1 ? (
          <label>
            Type
            <select value={category} onChange={(e) => changeCategory(e.target.value)}>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c === "Email" ? "Email account (SMTP / IMAP)" : c}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <div className="vault-form__entries">
        <div className="vault-form__entries-head">
          <span>Label</span>
          <span>Value</span>
          <span />
        </div>
        {entries.map((entry, i) => {
          const secret = isSecretLabel(entry.label);
          const isSecurity = /security$/i.test(entry.label) && meta?.security_choices;
          return (
            <div key={i} className="vault-form__entry">
              <input value={entry.label} onChange={(e) => setEntry(i, "label", e.target.value)} placeholder="Label" />
              <span className="vault-form__value">
                {isSecurity ? (
                  <select value={entry.value} onChange={(e) => setEntry(i, "value", e.target.value)}>
                    {meta.security_choices.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={secret && !reveal[i] ? "password" : "text"}
                    autoComplete="new-password"
                    value={entry.value}
                    onChange={(e) => setEntry(i, "value", e.target.value)}
                    placeholder="Value"
                  />
                )}
                {secret ? (
                  <button type="button" className="vault-icon" onClick={() => setReveal((r) => ({ ...r, [i]: !r[i] }))}>
                    {reveal[i] ? <FaEyeSlash /> : <FaEye />}
                  </button>
                ) : null}
              </span>
              <button
                type="button"
                className="vault-icon vault-icon--danger"
                title="Remove"
                onClick={() => setEntries((list) => list.filter((_, idx) => idx !== i))}
              >
                <FaTrash />
              </button>
            </div>
          );
        })}
        <div className="vault-form__entry-actions">
          <button type="button" className="vault-btn" onClick={() => setEntries((list) => [...list, { label: "", value: "" }])}>
            <FaPlus /> Add field
          </button>
          {presetEntries(meta, category).length ? (
            <button
              type="button"
              className="vault-btn"
              onClick={() => {
                const have = new Set(entries.map((e) => e.label.toLowerCase()));
                setEntries((list) => [...list, ...presetEntries(meta, category).filter((p) => !have.has(p.label.toLowerCase()))]);
              }}
            >
              Add {category} fields
            </button>
          ) : null}
          {category === "API" && !editing
            ? Object.entries(meta?.api_templates || {}).map(([provider, fields]) => (
                <button
                  key={provider}
                  type="button"
                  className="vault-btn"
                  title={`Fields Cloud Office reads for your own ${provider} app`}
                  onClick={() => {
                    setEntries(fields.map((f) => ({ label: f.label, value: f.value || "" })));
                    if (!name.trim()) setName(`${provider} app`);
                  }}
                >
                  Use {provider} app template
                </button>
              ))
            : null}
        </div>
        {category === "API" ? (
          <p className="vault-hint">
            Using your own Meta, Google Ads or LinkedIn app? Pick its template. Cloud Office then connects and syncs with
            your app instead of Tumma&apos;s.
          </p>
        ) : null}
        {category === "Email" ? (
          <p className="vault-hint">
            Gmail / Google Workspace and Outlook need an app password. Use SMTP Security SSL with port 465, or STARTTLS
            with port 587. IMAP is optional (used to read replies).
          </p>
        ) : null}
      </div>

      {category === "Email" ? (
        <label className="vault-check">
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />
          Use as the default sender for this company
        </label>
      ) : null}

      <div className="vault-actions">
        <button type="button" className="vault-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="submit" className="vault-btn vault-btn--primary" disabled={busy}>
          {busy ? "Saving…" : editing ? "Save changes" : "Save"}
        </button>
      </div>
    </form>
  );
}

export function TestResult({ result, onClose }) {
  if (!result) return null;
  const failed = result.smtp?.ok === false || result.imap?.ok === false;
  const line = (label, r) =>
    r ? (
      <div className={`vault-test__line ${r.ok ? "is-ok" : "is-bad"}`}>
        {r.ok ? <FaCheckCircle /> : <FaTimesCircle />} {label}: {r.message}
      </div>
    ) : null;
  return (
    <div className={`vault-test${onClose ? " vault-test--box" : ""}${failed ? " is-bad" : ""}`} role={failed ? "alert" : undefined}>
      {onClose ? (
        <button type="button" className="vault-test__close" aria-label="Dismiss test result" title="Dismiss" onClick={onClose}>
          <FaTimes />
        </button>
      ) : null}
      {line("SMTP", result.smtp)}
      {line("IMAP", result.imap)}
      {result.sent_to ? <div className="vault-test__line is-ok">Test email sent to {result.sent_to}</div> : null}
    </div>
  );
}

/**
 * Company vault: encrypted label/value items (email accounts, API keys, logins).
 * mode "owner" (portal, basePath "/client/email-accounts" or "/client/vault"): the owner, or a role
 * with the matching permission, opens it with their own login password; the password is kept in
 * memory for this panel so later edits do not ask again until "Lock". The API decides which
 * categories a basePath covers (meta.categories).
 * mode "staff" (basePath "/tenants/:id/vault"): item names, activity log and a temporary owner
 * password reset only; values are never sent to staff.
 * emailActions=false hides "Make default" / "Test connection" when another screen (Configs)
 * picks and tests the sender; `emailNote` is shown on Email items instead.
 */
function VaultPanel({
  basePath,
  title = "Vault",
  description,
  mode = "owner",
  changePasswordHref,
  addLabel = "Add item",
  emptyText = "Nothing here yet. Add email accounts (SMTP / IMAP), API keys or logins; values are encrypted before saving.",
  emailActions = true,
  emailNote = null,
  focusId = null,
}) {
  const staff = mode === "staff";
  const canOpen = !staff;
  const [needsChange, setNeedsChange] = useState(false);
  const [auditKey, setAuditKey] = useState(0);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [password, setPassword] = useState("");
  const [prompt, setPrompt] = useState(null);
  const [promptError, setPromptError] = useState("");
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [tests, setTests] = useState({});
  const [testTo, setTestTo] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [showLog, setShowLog] = useState(false);
  const [highlight, setHighlight] = useState(null);
  const focusedRef = useRef(null);
  const compact = useCompactView();
  const unlocked = !!password;
  // expanded[id] is the inner row shown under an item: "values", or "test" (last email test result).
  const audit = useVaultAudit(basePath, auditKey, showLog);
  const toggle = (id, mode) => setExpanded((x) => ({ ...x, [id]: x[id] === mode ? null : mode }));

  const loadLocked = useCallback(async () => {
    const res = await axiosInstance.get(basePath);
    const data = res.data?.data || {};
    setItems(data.items || []);
    setMeta(data.meta || null);
  }, [basePath]);

  const loadUnlocked = useCallback(
    async (pw) => {
      const res = await axiosInstance.post(`${basePath}/unlock`, { confirm_password: pw });
      const fresh = res.data?.data?.items || [];
      setItems(fresh);
      return fresh;
    },
    [basePath]
  );

  useEffect(() => {
    setPassword("");
    setLoading(true);
    loadLocked()
      .catch((err) => setError(apiError(err, "Could not load the vault.")))
      .finally(() => setLoading(false));
  }, [loadLocked]);

  // Opened from another screen for one item (e.g. Configs > Vault account): show, highlight and scroll to it.
  useEffect(() => {
    const id = Number(focusId);
    if (!id || loading || focusedRef.current === id || !items.some((i) => i.Vault_Id === id)) return undefined;
    focusedRef.current = id;
    setFilter("");
    setHighlight(id);
    const scroll = setTimeout(() => {
      const el = document.getElementById(`vault-item-${id}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    const clear = setTimeout(() => setHighlight(null), 2500);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
  }, [focusId, loading, items]);

  const refresh = () => (password ? loadUnlocked(password) : loadLocked());

  /** Run `action(password, unlockedItems)`, asking for the password first when locked. */
  const withPassword = (action) => {
    if (password) action(password, items);
    else {
      setPromptError("");
      setPrompt({ action });
    }
  };

  const submitPrompt = async (pw) => {
    setBusy(true);
    setPromptError("");
    try {
      const fresh = await loadUnlocked(pw);
      setPassword(pw);
      const next = prompt?.action;
      setPrompt(null);
      if (next) next(pw, fresh);
    } catch (err) {
      setPromptError(apiError(err, "Could not unlock."));
      setNeedsChange(err?.response?.data?.code === "password_change_required");
      setAuditKey((k) => k + 1);
    } finally {
      setBusy(false);
    }
  };

  const run = async (fn, success) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await fn();
      await refresh();
      setAuditKey((k) => k + 1);
      if (success) setNotice(success);
      return true;
    } catch (err) {
      if (err?.response?.data?.code === "password_required") setPassword("");
      setError(apiError(err));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = (body) =>
    withPassword(async (pw) => {
      const ok = await run(
        () =>
          editing?.Vault_Id
            ? axiosInstance.put(`${basePath}/${editing.Vault_Id}`, { ...body, confirm_password: pw })
            : axiosInstance.post(basePath, { ...body, confirm_password: pw }),
        "Saved."
      );
      if (ok) setEditing(null);
    });

  const remove = (item) => {
    if (!window.confirm(`Delete "${item.Name}"?`)) return;
    withPassword((pw) =>
      run(() => axiosInstance.delete(`${basePath}/${item.Vault_Id}`, { data: { confirm_password: pw } }), "Deleted.")
    );
  };

  const makeDefault = (item) => run(() => axiosInstance.post(`${basePath}/${item.Vault_Id}/default`), `${item.Name} is now the default sender.`);

  const test = async (item) => {
    setExpanded((x) => ({ ...x, [item.Vault_Id]: "test" }));
    setTests((t) => ({ ...t, [item.Vault_Id]: { loading: true } }));
    try {
      const res = await axiosInstance.post(
        `${basePath}/${item.Vault_Id}/test`,
        { send_to: testTo[item.Vault_Id] || undefined },
        { timeout: 60000 }
      );
      setTests((t) => ({ ...t, [item.Vault_Id]: res.data?.data }));
    } catch (err) {
      setTests((t) => ({ ...t, [item.Vault_Id]: { smtp: { ok: false, message: apiError(err, "Test failed") } } }));
    }
    setAuditKey((k) => k + 1);
  };

  const clearTest = (id) => {
    setTests((t) => ({ ...t, [id]: null }));
    setExpanded((x) => (x[id] === "test" ? { ...x, [id]: null } : x));
  };

  const unlockToView = () =>
    withPassword((_pw, fresh) => setExpanded(Object.fromEntries((fresh || []).map((i) => [i.Vault_Id, "values"]))));

  const viewValues = (item) => {
    if (expanded[item.Vault_Id] === "values") toggle(item.Vault_Id, "values");
    else withPassword(() => setExpanded((x) => ({ ...x, [item.Vault_Id]: "values" })));
  };

  const lock = () => {
    setPassword("");
    setEditing(null);
    setExpanded({});
    loadLocked().catch(() => {});
  };

  const categories = meta?.categories || DEFAULT_CATEGORIES;
  const visible = filter ? items.filter((i) => i.Category === filter) : items;

  const emailTestControls = (item) => (
    <>
      <input
        type="email"
        className="vault-input"
        placeholder="Send a test email to (optional)"
        value={testTo[item.Vault_Id] || ""}
        onChange={(e) => setTestTo((t) => ({ ...t, [item.Vault_Id]: e.target.value }))}
      />
      <button type="button" className="vault-btn vault-btn--sm" onClick={() => test(item)} disabled={tests[item.Vault_Id]?.loading}>
        <FaPlug /> {tests[item.Vault_Id]?.loading ? "Testing…" : "Test connection"}
      </button>
    </>
  );

  const makeDefaultButton = (item) =>
    canOpen && emailActions && item.Category === "Email" && !item.Is_Default ? (
      <button type="button" className="vault-btn vault-btn--sm" onClick={() => makeDefault(item)} disabled={busy}>
        <FaStar /> Make default
      </button>
    ) : null;

  const itemActions = (item, { withDefault = true } = {}) => (
    <div className="vault-row-actions">
      {withDefault ? makeDefaultButton(item) : null}
      {canOpen ? (
        <RowAction
          tone="view"
          title={expanded[item.Vault_Id] === "values" ? `Hide values of ${item.Name}` : `View values of ${item.Name}`}
          onClick={() => viewValues(item)}
          active={expanded[item.Vault_Id] === "values"}
        >
          <FaEye size={16} />
        </RowAction>
      ) : null}
      {canOpen ? (
        <>
          <RowAction
            tone="edit"
            title={`Edit ${item.Name}`}
            onClick={() => withPassword((_pw, fresh) => setEditing(fresh.find((i) => i.Vault_Id === item.Vault_Id) || item))}
          >
            <FaEdit size={16} />
          </RowAction>
          <RowAction tone="delete" title={`Delete ${item.Name}`} onClick={() => remove(item)}>
            <FaTrash size={16} />
          </RowAction>
        </>
      ) : null}
    </div>
  );

  const nameCell = (item) => (
    <span className="vault-cell-name">
      <strong>{item.Name}</strong>
      {item.Is_Default ? (
        <span className="vault-badge vault-badge--default">
          <FaStar /> Default sender
        </span>
      ) : null}
    </span>
  );

  const detail = (item) =>
    expanded[item.Vault_Id] === "values" ? (
      <div className="vault-detail">
        {item.Error ? <div className="vault-alert vault-alert--error">{item.Error}</div> : null}
        {unlocked && item.Entries ? (
          <dl className="vault-entries">
            {item.Entries.map((e) => (
              <div key={e.label} className="vault-entry">
                <dt>{e.label}</dt>
                <dd>
                  <SecretValue value={e.value} secret={e.secret} />
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          <div className="vault-locked">
            <FaLock aria-hidden="true" /> Values hidden
          </div>
        )}
        {item.Category === "Email" && !emailActions && emailNote ? <div className="vault-hint">{emailNote}</div> : null}
      </div>
    ) : tests[item.Vault_Id] && !tests[item.Vault_Id].loading ? (
      <div className="vault-detail">
        <TestResult result={tests[item.Vault_Id]} onClose={() => clearTest(item.Vault_Id)} />
      </div>
    ) : null;

  const showTestColumn = canOpen && emailActions && visible.some((i) => i.Category === "Email");
  const columnCount = 4 + (showTestColumn ? 1 : 0) + (canOpen ? 1 : 0);

  return (
    <div className="vault">
      {title ? (
        <h3 className="vault-title">
          <FaKey aria-hidden="true" /> {title}
        </h3>
      ) : null}
      <div className="vault-toolbar">
        <div className="vault-actions">
          {canOpen ? (
            unlocked ? (
              <button type="button" className="vault-btn vault-btn--lock" onClick={lock}>
                <FaLock /> Lock
              </button>
            ) : (
              <button type="button" className="vault-btn vault-btn--lock" onClick={unlockToView} disabled={!items.length}>
                <FaLockOpen /> Unlock to view
              </button>
            )
          ) : null}
          {canOpen && !editing ? (
            <button
              type="button"
              className="vault-btn vault-btn--primary vault-btn--add"
              onClick={() => withPassword(() => setEditing({}))}
            >
              <FaPlus /> {addLabel}
            </button>
          ) : null}
          <ActivityButton open={showLog} onToggle={() => setShowLog((v) => !v)} className="vault-btn--log" />
          <InfoTip text={description} />
        </div>
      </div>

      {showLog ? (
        <div className="vault-log">
          <AuditTable entries={audit.entries} error={audit.error} />
        </div>
      ) : null}

      {!canOpen ? (
        <div className="vault-alert vault-alert--info">
          <FaLock aria-hidden="true" /> Only the client&apos;s owner, or team members whose role has access, can open
          this vault. Staff see item names and the activity log only.
        </div>
      ) : null}
      {staff ? <OwnerPasswordReset basePath={basePath} onDone={() => setAuditKey((k) => k + 1)} /> : null}

      {prompt ? (
        <PasswordPrompt
          busy={busy}
          error={promptError}
          text="Confirm your login password to view and change these values."
          onSubmit={submitPrompt}
          onCancel={() => {
            setPrompt(null);
            setNeedsChange(false);
          }}
        />
      ) : null}
      {needsChange && changePasswordHref ? (
        <div className="vault-alert vault-alert--info">
          <Link to={changePasswordHref}>Change your password</Link>, then come back to open the vault.
        </div>
      ) : null}
      {error ? <div className="vault-alert vault-alert--error">{error}</div> : null}
      {notice ? <div className="vault-alert vault-alert--ok">{notice}</div> : null}

      {editing && unlocked ? (
        <VaultForm
          key={editing.Vault_Id || "new"}
          meta={meta}
          item={editing.Vault_Id ? editing : null}
          busy={busy}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      ) : null}

      {categories.length > 1 ? (
        <div className="vault-tabs">
          {["", ...categories].map((c) => (
            <button key={c || "all"} type="button" className={`vault-tab${filter === c ? " is-active" : ""}`} onClick={() => setFilter(c)}>
              {c || "All"}
              <span>{c ? items.filter((i) => i.Category === c).length : items.length}</span>
            </button>
          ))}
        </div>
      ) : null}

      {loading ? <div className="vault-empty">Loading…</div> : null}
      {!loading && !visible.length ? (
        <div className="vault-empty">
          {canOpen ? emptyText : "The client has not added anything to the vault yet."}
        </div>
      ) : null}

      {visible.length && !compact ? (
        <div className="vault-table__wrap">
          <table className="vault-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Values</th>
                <th>Updated</th>
                {showTestColumn ? <th>Test email to</th> : null}
                {canOpen ? <th className="vault-table__actions">Actions</th> : null}
              </tr>
            </thead>
            <tbody>
              {visible.map((item, index) => {
                const Icon = CATEGORY_ICONS[item.Category] || FaKey;
                const inner = expanded[item.Vault_Id] ? detail(item) : null;
                const open = !!inner;
                const rowClass = [
                  index % 2 ? "is-alt" : "",
                  open ? "is-open" : "",
                  highlight === item.Vault_Id ? "is-focus" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <React.Fragment key={item.Vault_Id}>
                    <tr id={`vault-item-${item.Vault_Id}`} className={rowClass}>
                      <td>{nameCell(item)}</td>
                      <td>
                        <span className="vault-badge">
                          <Icon aria-hidden="true" /> {item.Category}
                        </span>
                      </td>
                      <td className="vault-cell-sub">
                        {unlocked && item.Entries ? (
                          `${item.Entries.length} field${item.Entries.length === 1 ? "" : "s"}`
                        ) : (
                          <>
                            <FaLock aria-hidden="true" /> Hidden
                          </>
                        )}
                      </td>
                      <td className="vault-cell-sub">{fmtWhen(item.Updated_On || item.Created_On)}</td>
                      {showTestColumn ? (
                        <td>
                          {item.Category === "Email" ? <div className="vault-item__test vault-item__test--inline">{emailTestControls(item)}</div> : null}
                        </td>
                      ) : null}
                      {canOpen ? <td className="vault-table__actions">{itemActions(item)}</td> : null}
                    </tr>
                    {open ? (
                      <tr className={`vault-table__detail${index % 2 ? " is-alt" : ""}`}>
                        <td colSpan={columnCount}>{inner}</td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {visible.length && compact ? (
        <div className="vault-list">
          {visible.map((item) => {
            const Icon = CATEGORY_ICONS[item.Category] || FaKey;
            const showValues = expanded[item.Vault_Id] === "values";
            return (
              <article
                key={item.Vault_Id}
                id={`vault-item-${item.Vault_Id}`}
                className={`vault-item${highlight === item.Vault_Id ? " is-focus" : ""}`}
              >
                <div className="vault-item__head">
                  <span className="vault-item__icon">
                    <Icon aria-hidden="true" />
                    {showValues && unlocked ? null : (
                      <span className="vault-item__lock" title="Values hidden">
                        <FaLock aria-label="Values hidden" />
                      </span>
                    )}
                  </span>
                  <div className="vault-item__main">
                    <strong className="vault-item__title">{item.Name}</strong>
                    <div className="vault-item__badges">
                      <span className="vault-badge">{item.Category}</span>
                      {item.Is_Default ? (
                        <span className="vault-badge vault-badge--default">
                          <FaStar /> Default sender
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                {makeDefaultButton(item) ? <div className="vault-item__extra">{makeDefaultButton(item)}</div> : null}
                {showValues ? detail(item) : null}
                {item.Category === "Email" && canOpen && emailActions ? (
                  <div className="vault-item__test">{emailTestControls(item)}</div>
                ) : null}
                {expanded[item.Vault_Id] === "test" ? detail(item) : null}
                {canOpen ? <div className="vault-item__actions">{itemActions(item, { withDefault: false })}</div> : null}
              </article>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

export default VaultPanel;
